import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { EMPTY } from 'rxjs';
import { environment } from '@env/environment';
import { PreviewSessionService } from '@core/services/preview-session.service';

/** Task-domain paths a preview must never be allowed to *mutate*. */
const TASK_DOMAIN_PATTERNS = [
  '/task',
  '/member-task',
  '/tag',
  '/thread',
  '/comment',
];

/**
 * Read endpoints the backend actually serves in preview (mirrors the
 * `@AllowPreview()` GET routes). The scoped token is attached to *these only*;
 * tagging any other endpoint (auth, notifications, terms, links) would make the
 * backend reject the call with 403 "not available in preview mode".
 *
 * `/threads/task/` is the task-keyed read-only discussion so a mentioned member
 * can see the conversation; every other `/threads` route stays blocked.
 */
const PREVIEW_READABLE_PATTERNS = [
  '/task',
  '/member-task',
  '/tag',
  '/threads/task/',
];

function isApiUrl(url: string): boolean {
  return url.includes(environment.apiUrl);
}

function isPreviewExchange(url: string): boolean {
  return url.includes('/preview/session/');
}

function isTaskDomainUrl(url: string): boolean {
  return TASK_DOMAIN_PATTERNS.some((pattern) => url.includes(pattern));
}

function isPreviewReadableUrl(url: string): boolean {
  return PREVIEW_READABLE_PATTERNS.some((pattern) => url.includes(pattern));
}

/**
 * Attaches the scoped preview token to the whitelisted read endpoints while a
 * preview session is active. Runs first so it can short-circuit before
 * loading/telemetry kick in.
 *
 * - A whitelisted GET read gets the token → backend answers 200, or 403 if the
 *   task is out of scope; never 401, so session-expired handling never fires.
 * - A mutating request against the task domain is a real user action: we stop
 *   it and interpose the login instead of silently failing.
 * - Every other call (auth/login, notifications, terms, threads, links) is left
 *   untouched so it is never tagged with the preview token.
 */
export const previewAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const preview = inject(PreviewSessionService);

  if (!preview.isActive() || !isApiUrl(req.url) || isPreviewExchange(req.url)) {
    return next(req);
  }

  if (req.method !== 'GET' && isTaskDomainUrl(req.url)) {
    preview.requireLogin();
    return EMPTY;
  }

  const token = preview.getToken();
  if (!token || req.method !== 'GET' || !isPreviewReadableUrl(req.url)) {
    return next(req);
  }

  return next(
    req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }),
  );
};
